import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  IconButton,
  Paper,
  Grid,
  Avatar,
  CircularProgress,
  Card,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  Badge,
  TablePagination,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Person,
  VerifiedUser,
  Phone,
  Email,
  CheckCircle,
  WatchLater,
  History,
  Refresh,
  FileCopy,
  Close,
} from "@material-ui/icons";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import HttpService from "../../services/httpService";
import AadhaarReveal from "../../components/AadhaarReveal";

// ─── STYLES ────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },

  header: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    background: "linear-gradient(135deg, #1A3C6E 0%, #2E75B6 100%)",
    borderRadius: theme.spacing(2),
    color: "#fff",
    boxShadow: "0 8px 32px rgba(26, 60, 110, 0.3)",
  },
  headerTitle: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerSubtitle: {
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
  },
  tabs: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
    },
  },
  tabLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 500,
  },
  badge: {
    marginLeft: theme.spacing(1),
    "& .MuiBadge-badge": {
      fontSize: "0.7rem",
      height: 20,
      minWidth: 20,
      borderRadius: 10,
    },
  },
  card: {
    borderRadius: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
    },
    marginBottom: theme.spacing(2),
    cursor: "pointer",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #f0f0f0",
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  statusChip: {
    fontWeight: 600,
    padding: theme.spacing(0.5, 1.5),
    "&.pending": {
      backgroundColor: "#fff3e0",
      color: "#e65100",
    },
    "&.pending_verification": {
      backgroundColor: "#fff3e0",
      color: "#e65100",
    },
    "&.approved": {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
    },
    "&.rejected": {
      backgroundColor: "#fbe9e7",
      color: "#c62828",
    },
    "&.active": {
      backgroundColor: "#e3f2fd",
      color: "#0d47a1",
    },
  },
  approverAvatar: {
    width: 32,
    height: 32,
    fontSize: "0.75rem",
    backgroundColor: "#667eea",
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid #f5f5f5",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  infoLabel: {
    color: "#666",
    fontWeight: 500,
  },
  infoValue: {
    fontWeight: 500,
    color: "#333",
  },
  historyTimeline: {
    position: "relative",
    paddingLeft: theme.spacing(3),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: "#e0e0e0",
    },
  },
  historyItem: {
    position: "relative",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f8f9fa",
    borderRadius: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: -7,
      top: 16,
      width: 12,
      height: 12,
      borderRadius: "50%",
      backgroundColor: "#667eea",
      border: "2px solid #fff",
    },
    "&.approved::before": {
      backgroundColor: "#4caf50",
    },
    "&.rejected::before": {
      backgroundColor: "#f44336",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: "#999",
  },
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  verifiedBadge: {
    backgroundColor: "#4caf50",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: "0.7rem",
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
}));

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────
// Read-only status view: shows every pending / approved / rejected
// rental-owner & service-provider onboarding record. No approve/reject here —
// that only happens on the approval dashboard. Edit is available to everyone.
const OnboardingApprovalStatusView = function (props) {
  const classes = useStyles();
  const [loading, setLocalLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modelType, setModelType] = useState("rental");
  const [reviewStep, setReviewStep] = useState(0);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [viewerIsPrivileged, setViewerIsPrivileged] = useState(false);

  const DEFAULT_PAGE_SIZE = 200;
  // TablePagination uses 0-indexed pages; the API is 1-indexed.
  const [pendingPage, setPendingPage] = useState(0);
  const [pendingRowsPerPage, setPendingRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [historyTotal, setHistoryTotal] = useState(0);

  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0,
  });

  // ── Fetch ALL pending records (not filtered to this user's approvals) ──
  const fetchPendingRecords = useCallback(async () => {
    try {
      setLocalLoading(true);
      const res = await HttpService.getAllPendingApprovals(modelType, {
        page: pendingPage + 1,
        limit: pendingRowsPerPage,
      });
      const data = res?.data?.data || [];
      setPendingRecords(data);
      setPendingTotal(res?.data?.pagination?.total ?? data.length);
      if (typeof res?.data?.viewerIsPrivileged === "boolean") {
        setViewerIsPrivileged(res.data.viewerIsPrivileged);
      }
    } catch (err) {
      props.setSnackInfo("Failed to fetch pending records", "error");
    } finally {
      setLocalLoading(false);
    }
  }, [modelType, pendingPage, pendingRowsPerPage]);

  // ── Fetch Approved / Rejected History ───────────────────────────────────
  const fetchHistoryRecords = useCallback(async () => {
    try {
      setLocalLoading(true);
      const res = await HttpService.getApprovedHistory(modelType, {
        page: historyPage + 1,
        limit: historyRowsPerPage,
        onlyMine: true,
      });
      const data = res?.data?.data || [];
      setHistoryRecords(data);
      setHistoryTotal(res?.data?.pagination?.total ?? data.length);
      const statusCounts = res?.data?.statusCounts;
      if (statusCounts) {
        setStats((s) => ({ ...s, approved: statusCounts.active || 0, rejected: statusCounts.rejected || 0 }));
      }
    } catch (err) {
      props.setSnackInfo("Failed to fetch history", "error");
    } finally {
      setLocalLoading(false);
    }
  }, [modelType, historyPage, historyRowsPerPage]);

  useEffect(() => {
    fetchPendingRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelType, pendingPage, pendingRowsPerPage]);

  useEffect(() => {
    fetchHistoryRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelType, historyPage, historyRowsPerPage]);

  const handleModelTypeChange = (value) => {
    setModelType(value);
    setPendingPage(0);
    setHistoryPage(0);
  };

  useEffect(() => {
    setStats((s) => ({ ...s, pending: pendingTotal, total: pendingTotal + historyTotal }));
  }, [pendingTotal, historyTotal]);

  // ─── View Record Details ──────────────────────────────────────────────
  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setReviewStep(0);
    setDialogOpen(true);
  };

  // ─── Edit (navigates to the onboarding form in edit mode) — available to everyone ──
  const handleEditRecord = (record) => {
    window.open(
      `/rental-onboarding/for-miscpayment?editId=${record._id}&editType=${modelType}`,
      "_blank"
    );
  };

  // ─── Bulk-download all attachments as a zip ─────────────────────────────
  const handleDownloadAllAttachments = async (record) => {
    setDownloadingZip(true);
    try {
      const res = modelType === "rental"
        ? await HttpService.downloadOwnerDocumentsZip(record._id)
        : await HttpService.downloadServiceProviderDocumentsZip(record._id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${(record.owner_name || record.name || "documents").replace(/\s+/g, "_")}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      props.setSnackInfo("Failed to download attachments", "error");
    } finally {
      setDownloadingZip(false);
    }
  };

  // ─── Documents matching a set of doc_types, rendered as open-in-new-tab links ──
  const renderDocLinks = (record, docTypes, emptyLabel = "No documents uploaded") => {
    const docs = (record?.documents || []).filter((d) => docTypes.includes(d.doc_type));
    if (!docs.length) {
      return <Typography variant="caption" color="textSecondary">{emptyLabel}</Typography>;
    }
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {docs.map((d, i) => (
          <Chip
            key={i}
            size="small"
            icon={<FileCopy style={{ fontSize: 14 }} />}
            label={d.original_name || d.doc_type}
            component="a"
            href={d.file_path}
            target="_blank"
            rel="noopener noreferrer"
            clickable
            style={{ maxWidth: 220 }}
          />
        ))}
      </div>
    );
  };

  // ─── Section-wise review steps (mirrors the onboarding form's sections) ────
  const getReviewSteps = (record) => {
    if (!record) return [];
    if (modelType === "rental") {
      return [
        {
          label: "Identity",
          content: (
            <Box>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Experience Centre</span>
                <span className={classes.infoValue}>{record.experienceCenterId?.name || "—"}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Owner Name</span>
                <span className={classes.infoValue}>{record.owner_name}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Remarks</span>
                <span className={classes.infoValue}>{record.remarks || "—"}</span>
              </div>
            </Box>
          ),
        },
        {
          label: "Tax & Compliance",
          content: (
            <Box>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>PAN</span>
                <span className={classes.infoValue}>{record.pan_number}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Aadhaar</span>
                <span className={classes.infoValue}>
                  {modelType === "rental"
                    ? <AadhaarReveal ownerId={record._id} masked={record.aadhaar_masked} setSnackInfo={props.setSnackInfo} />
                    : (record.aadhaar_masked || "Encrypted")}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>GST</span>
                <span className={classes.infoValue}>
                  {record.gst_applicable ? `${record.gst_number} (${record.gst_percentage}%)` : "Not applicable"}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>TDS</span>
                <span className={classes.infoValue}>
                  {record.tds_applicable ? `${record.tds_percentage}%` : "Not applicable"}
                </span>
              </div>
              <Box mt={1.5}>
                <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Attachments
                </Typography>
                {renderDocLinks(record, ["aadhaar", "pan", "gst_certificate"])}
              </Box>
            </Box>
          ),
        },
        {
          label: "Bank Details",
          content: (
            <Box>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Account Name</span>
                <span className={classes.infoValue}>{record.bank?.account_name}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Account Number</span>
                <span className={classes.infoValue}>{record.bank?.account_number}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Bank / IFSC</span>
                <span className={classes.infoValue}>{record.bank?.bank_name} - {record.bank?.ifsc_code}</span>
              </div>
              <Box mt={1.5}>
                <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Attachments
                </Typography>
                {renderDocLinks(record, ["cancelled_cheque"])}
              </Box>
            </Box>
          ),
        },
        {
          label: "Property",
          content: (
            <Box>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Address</span>
                <span className={classes.infoValue}>
                  {record.address?.line1}, {record.address?.city}, {record.address?.state} - {record.address?.pincode}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Monthly Rent</span>
                <span className={classes.infoValue}>₹{record.rent_amount}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Rental Start Date</span>
                <span className={classes.infoValue}>
                  {record.rental_start_date ? new Date(record.rental_start_date).toLocaleDateString() : "—"}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Rent Increment</span>
                <span className={classes.infoValue}>
                  Every {record.rent_increment_period_months || 12} month(s) ·{" "}
                  {record.yearly_increment_value
                    ? `${record.yearly_increment_value}${record.yearly_increment_type === "percentage" ? "%" : " ₹"}`
                    : "—"}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Due / Penalty</span>
                <span className={classes.infoValue}>
                  Day {record.due_day}, grace till {record.max_payment_day} · ₹{record.penalty_per_day}/day penalty
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Agreement Period</span>
                <span className={classes.infoValue}>
                  {record.agreement_effective_date ? new Date(record.agreement_effective_date).toLocaleDateString() : "—"}
                  {" → "}
                  {record.agreement_expiry_date ? new Date(record.agreement_expiry_date).toLocaleDateString() : "—"}
                </span>
              </div>
              <Box mt={1.5}>
                <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Attachments
                </Typography>
                {renderDocLinks(record, ["rental_agreement"])}
              </Box>
            </Box>
          ),
        },
        {
          label: "Contacts",
          content: (
            <Box>
              {(record.contact_persons || []).map((cp, i) => (
                <Box key={i} mb={1.5} p={1.5} style={{ background: "#f8f9fa", borderRadius: 8 }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {cp.name} {i === 0 && <Chip size="small" label="Primary" style={{ marginLeft: 6 }} />}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {cp.primary_mobile} · {cp.email}
                  </Typography>
                </Box>
              ))}
              <Box mt={1.5}>
                <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Other Documents
                </Typography>
                {renderDocLinks(record, ["other"], "None uploaded")}
              </Box>
            </Box>
          ),
        },
      ];
    }

    // ── Service Provider ──
    return [
      {
        label: "Identity",
        content: (
          <Box>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Name</span>
              <span className={classes.infoValue}>{record.name}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Mobile</span>
              <span className={classes.infoValue}>{record.mobile || "N/A"}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Email</span>
              <span className={classes.infoValue}>{record.email || "N/A"}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Remarks</span>
              <span className={classes.infoValue}>{record.remarks || "—"}</span>
            </div>
          </Box>
        ),
      },
      {
        label: "Tax & Compliance",
        content: (
          <Box>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>PAN</span>
              <span className={classes.infoValue}>{record.pan_number}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>GST</span>
              <span className={classes.infoValue}>
                {record.gst_applicable ? record.gst_number : "Not applicable"}
              </span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>TDS</span>
              <span className={classes.infoValue}>
                {record.tds_applicable ? `${record.tds_percentage}%` : "Not applicable"}
              </span>
            </div>
            <Box mt={1.5}>
              <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                Attachments
              </Typography>
              {renderDocLinks(record, ["aadhaar", "pan"])}
            </Box>
          </Box>
        ),
      },
      {
        label: "Bank Details",
        content: (
          <Box>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Account Name</span>
              <span className={classes.infoValue}>{record.bank?.account_name}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Account Number</span>
              <span className={classes.infoValue}>{record.bank?.account_number}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Bank / IFSC</span>
              <span className={classes.infoValue}>{record.bank?.bank_name} - {record.bank?.ifsc_code}</span>
            </div>
            <Box mt={1.5}>
              <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                Attachments
              </Typography>
              {renderDocLinks(record, ["cancelled_cheque"])}
            </Box>
          </Box>
        ),
      },
      {
        label: "Locations & Scopes",
        content: (
          <Box>
            {(record.location_mappings || []).map((m, i) => (
              <Box key={i} mb={1.5} p={1.5} style={{ background: "#f8f9fa", borderRadius: 8 }}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {m.experienceCenterId?.name || "Unknown location"}
                </Typography>
                {(m.scopes || []).map((s, j) => (
                  <Typography key={j} variant="caption" color="textSecondary" display="block">
                    {s.service_type === "others" ? s.service_type_other : s.service_type}
                    {s.max_amount ? ` · cap ₹${s.max_amount}` : ""}
                  </Typography>
                ))}
              </Box>
            ))}
            <Box mt={1.5}>
              <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                Other Documents
              </Typography>
              {renderDocLinks(record, ["other"], "None uploaded")}
            </Box>
          </Box>
        ),
      },
    ];
  };

  // ─── Render Record Card ────────────────────────────────────────────────
  const renderRecordCard = (record) => {
    const isApproved = record.status === "active";

    // Get display name based on model type
    const displayName = modelType === "rental"
      ? record.owner_name
      : record.name || "Unnamed";

    // Get contact person name
    const contactPerson = modelType === "rental"
      ? record.contact_persons?.[0]?.name
      : record.name || "N/A";

    // Get mobile number
    const mobileNumber = modelType === "rental"
      ? record.mobile_numbers?.[0]
      : record.mobile || "N/A";

    // Get email
    const emailAddress = modelType === "rental"
      ? record.email_ids?.[0]
      : record.email || "N/A";

    return (
      <Card className={classes.card} onClick={() => handleViewRecord(record)}>
        <div className={classes.cardHeader}>
          <Box>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {displayName}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
              {modelType === "rental" ? (
                <Chip
                  size="small"
                  label={record.experienceCenterId?.name || "No EC"}
                  variant="outlined"
                  style={{ fontSize: "0.7rem" }}
                />
              ) : (
                (record.location_mappings || []).map((m, i) => (
                  <Chip
                    key={i}
                    size="small"
                    label={m.experienceCenterId?.name || "No location"}
                    variant="outlined"
                    style={{ fontSize: "0.7rem" }}
                  />
                ))
              )}
              <Chip
                size="small"
                label={record.status?.replace("_", " ").toUpperCase()}
                className={`${classes.statusChip} ${record.status}`}
              />
              {isApproved && record.verified_at && (
                <span className={classes.verifiedBadge}>
                  <CheckCircle style={{ fontSize: 14, marginRight: 4 }} />
                  Verified
                </span>
              )}
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleEditRecord(record);
              }}
            >
              Edit
            </Button>
          </Box>
        </div>
        <div className={classes.cardContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2">
                  {contactPerson}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <Phone fontSize="small" color="action" />
                <Typography variant="body2">
                  {mobileNumber}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2" noWrap>
                  {emailAddress}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {record.status === "pending_verification" && record.currentApprovers?.length > 0 && (
            <Box mt={2} pt={2} borderTop="1px solid #f0f0f0">
              <Typography variant="caption" color="textSecondary">
                Current Approvers:
              </Typography>
              <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                {record.currentApprovers.map((approver, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    avatar={<Avatar className={classes.approverAvatar}>
                      {approver.name?.charAt(0) || "A"}
                    </Avatar>}
                    label={approver.name || "Unknown"}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {isApproved && record.verified_by && (
            <Box mt={2} pt={2} borderTop="1px solid #f0f0f0">
              <Typography variant="caption" color="textSecondary">
                Verified By:
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {record.verified_by?.name || "System"}
                {record.verified_at && (
                  <span style={{ color: "#666", fontWeight: 400, marginLeft: 8 }}>
                    on {new Date(record.verified_at).toLocaleDateString()}
                  </span>
                )}
              </Typography>
            </Box>
          )}
        </div>
      </Card>
    );
  };

  // ─── Render Dialog ─────────────────────────────────────────────────────
  const renderDialog = () => {
    const steps = getReviewSteps(selectedRecord);

    return (
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" style={{ fontWeight: 600 }}>
                {modelType === "rental" ? selectedRecord?.owner_name : selectedRecord?.name}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Chip
                  size="small"
                  label={selectedRecord?.status?.toUpperCase()}
                  className={`${classes.statusChip} ${selectedRecord?.status}`}
                />
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={8}>
              <Button
                size="small"
                variant="outlined"
                startIcon={downloadingZip ? <CircularProgress size={14} /> : <FileCopy />}
                disabled={downloadingZip}
                onClick={() => handleDownloadAllAttachments(selectedRecord)}
                style={{ marginRight: 8 }}
              >
                Download All Attachments
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleEditRecord(selectedRecord)}
                style={{ marginRight: 8 }}
              >
                Edit
              </Button>
              <IconButton onClick={() => setDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          {selectedRecord && (
            <>
              <Stepper activeStep={reviewStep} alternativeLabel style={{ padding: "8px 0 24px" }}>
                {steps.map((s) => (
                  <Step key={s.label}>
                    <StepLabel>{s.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box minHeight={220}>
                {steps[reviewStep]?.content}
              </Box>

              <Box display="flex" justifyContent="space-between" mt={3}>
                <Button
                  disabled={reviewStep === 0}
                  onClick={() => setReviewStep((s) => s - 1)}
                >
                  Back
                </Button>
                <Typography variant="caption" color="textSecondary" style={{ alignSelf: "center" }}>
                  Section {reviewStep + 1} of {steps.length}
                </Typography>
                <Button
                  disabled={reviewStep === steps.length - 1}
                  onClick={() => setReviewStep((s) => s + 1)}
                >
                  Next
                </Button>
              </Box>

              {/* Approval History — always visible regardless of section */}
              {selectedRecord.approvalHistory?.length > 0 && (
                <Box mt={4}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    <History fontSize="small" /> Approval History
                  </Typography>
                  <Divider style={{ margin: "8px 0 16px" }} />
                  <Box className={classes.historyTimeline}>
                    {selectedRecord.approvalHistory.map((item, idx) => (
                      <div
                        key={idx}
                        className={`${classes.historyItem} ${item.action}`}
                      >
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            Level {item.level} - {item.action?.toUpperCase()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(item.actedAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          By: {item.actedBy?.name || "Unknown"}
                        </Typography>
                        {item.remarks && (
                          <Typography variant="caption" style={{ color: "#666" }}>
                            Remarks: {item.remarks}
                          </Typography>
                        )}
                      </div>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      {/* Header */}
      <Paper className={classes.header}>
        <Typography variant="h4" className={classes.headerTitle}>
          <VerifiedUser fontSize="large" />
          Onboarding Verification Status
        </Typography>
        <Typography variant="body1" className={classes.headerSubtitle}>
          Track rental owner &amp; service provider onboarding requests — pending, approved and rejected. This is a read-only status view.
          {viewerIsPrivileged
            ? " You're on the privileged viewers list, so you're seeing every onboarder's records."
            : " Records shown are your own, unless you're on the privileged viewers list."}
        </Typography>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Pending</Typography>
            <Typography variant="h4" style={{ color: "#ff9800", fontWeight: 600 }}>
              {stats.pending}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Approved</Typography>
            <Typography variant="h4" style={{ color: "#4caf50", fontWeight: 600 }}>
              {stats.approved}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Rejected</Typography>
            <Typography variant="h4" style={{ color: "#f44336", fontWeight: 600 }}>
              {stats.rejected}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Total</Typography>
            <Typography variant="h4" style={{ color: "#667eea", fontWeight: 600 }}>
              {stats.total}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper className={classes.tabs}>
        <Tabs
          value={tabValue}
          onChange={(e, val) => setTabValue(val)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <div className={classes.tabLabel}>
                <WatchLater fontSize="small" />
                Pending
                <Badge badgeContent={stats.pending} color="secondary" className={classes.badge} />
              </div>
            }
          />
          <Tab
            label={
              <div className={classes.tabLabel}>
                <History fontSize="small" />
                History
                <Badge badgeContent={stats.approved + stats.rejected} color="primary" className={classes.badge} />
              </div>
            }
          />
        </Tabs>
      </Paper>

      {/* Model Type Selector */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Record Type</InputLabel>
          <Select
            value={modelType}
            onChange={(e) => handleModelTypeChange(e.target.value)}
            label="Record Type"
          >
            <MenuItem value="rental">Rental Owners</MenuItem>
            <MenuItem value="service_provider">Service Providers</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={() => {
            fetchPendingRecords();
            fetchHistoryRecords();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Content */}
      {loading ? (
        <div className={classes.loadingOverlay}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {tabValue === 0 && (
            <>
              {pendingRecords.length === 0 ? (
                <Paper className={classes.emptyState}>
                  <CheckCircle style={{ fontSize: 60, color: "#4caf50", marginBottom: 16 }} />
                  <Typography variant="h6">No pending records</Typography>
                  <Typography variant="body2" color="textSecondary">
                    All records are up to date. Check back later for new requests.
                  </Typography>
                </Paper>
              ) : (
                <>
                  {pendingRecords.map((item) => renderRecordCard(item))}
                  <Paper style={{ borderRadius: 12 }}>
                    <TablePagination
                      component="div"
                      count={pendingTotal}
                      page={pendingPage}
                      onChangePage={(e, newPage) => setPendingPage(newPage)}
                      rowsPerPage={pendingRowsPerPage}
                      onChangeRowsPerPage={(e) => {
                        setPendingRowsPerPage(parseInt(e.target.value, 10));
                        setPendingPage(0);
                      }}
                      rowsPerPageOptions={[50, 100, 200, 500]}
                    />
                  </Paper>
                </>
              )}
            </>
          )}
          {tabValue === 1 && (
            <>
              {historyRecords.length === 0 ? (
                <Paper className={classes.emptyState}>
                  <History style={{ fontSize: 60, color: "#999", marginBottom: 16 }} />
                  <Typography variant="h6">No history available</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Approved and rejected records will appear here.
                  </Typography>
                </Paper>
              ) : (
                <>
                  {historyRecords.map((item) => renderRecordCard(item))}
                  <Paper style={{ borderRadius: 12 }}>
                    <TablePagination
                      component="div"
                      count={historyTotal}
                      page={historyPage}
                      onChangePage={(e, newPage) => setHistoryPage(newPage)}
                      rowsPerPage={historyRowsPerPage}
                      onChangeRowsPerPage={(e) => {
                        setHistoryRowsPerPage(parseInt(e.target.value, 10));
                        setHistoryPage(0);
                      }}
                      rowsPerPageOptions={[50, 100, 200, 500]}
                    />
                  </Paper>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Dialogs */}
      {renderDialog()}
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(OnboardingApprovalStatusView);
